//angular imports
import { ChangeDetectionStrategy, Component, computed, input, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

//local imports
import { RoomGroupModel } from '@app/catalog';
import { FormValueModel } from '@app/shared';


/**
 * @summary A model to hold the state for the current details form.
 */
export class RoomGroupDetails {

  /**
   * @summary The current entity name
   */
  public name: FormValueModel<string>;

  /**
   * @summary Indicates if the form has changes.
   */
  public hasChanges: Signal<boolean>;  
  
  /**
   * @summary Indicates if the form has validation issues
   */
  public isValid: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * @summary Indicates if the form is awaiting validation
   */
  public isPending: WritableSignal<boolean> = signal<boolean>(false);  

  /**
   * @summary Creates a new Details instance using the provided entity to prepopulate it.
   * @description This is used for the edit form
   * @param entity: the model for the entity being changed
   * @returns An instance of a details model for tracking changes
   */
  public static fromEntity(entity: RoomGroupModel)
  {
    let _this = new RoomGroupDetails();
    
    _this.name = FormValueModel.create(entity.name());
    
    _this.hasChanges = FormValueModel.hasChanges(
      [_this.name]
    );

    return _this;
  }

  /**
   * @summary Creates a new Details instance with no data.
   * @description This is used for the add form
   * @returns An instance of a details model for tracking changes
   */
  public static empty()
  {
    let _this = new RoomGroupDetails();
    
    _this.name = FormValueModel.create<string>(undefined);
    
    _this.hasChanges = FormValueModel.hasChanges(
      [_this.name]
    );

    return _this;
  }  
}


@Component({
  selector: 'room-group-details',
  standalone: false,
  templateUrl: './room-group-details.component.html',
  styleUrl: './room-group-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoomGroupDetailsComponent {

  private readonly destroying$ = new Subject<void>();
  
  public roomGroup = input.required<RoomGroupDetails>();

  public form: Signal<FormGroup> = computed(() => {
  
    let entity = this.roomGroup();
  
      let formParts = {
        name: [untracked( () => entity?.name.value() || ''),
          {
            validators: [
              Validators.required,
              Validators.pattern('^(?!\\s+$).+') //Cannot be whitespace
            ],
          }]
      };
  
      let form = this.formBuilder.group(formParts);
  
      // Subscribe to form value changes, emit events.
      form.valueChanges
        .pipe(takeUntil(this.destroying$))
        .subscribe(_ => {
  
          let rawValue = form.getRawValue();
  
          let details = this.roomGroup();
          
          details.name.value.set(rawValue.name);
        });
  
      //If the form changes, pass the validation status back into the model
      //
      //NB: Setting the signal in a computed signal can cause problems if the value is set before the signal is complete.
      //    In this case it doesn't occur because the validation is changed by the async validator configured on the form, it has a
      //    debounceTime of 500ms so this is enough to allow this computed signal to complete.
      form.statusChanges
        .pipe(takeUntil(this.destroying$))
        .subscribe(_ => { 
          let details = this.roomGroup();
  
          if (form.pending) 
          {
            details.isPending.set(true); 
            return;
          }
          
          details.isPending.set(false); 
          details.isValid.set(form.valid);         
        });
  
      return form;
    });

      constructor(
        private formBuilder: FormBuilder) {    
      }
    
      ngOnDestroy(): void {
    
        this.destroying$.next(undefined);
        this.destroying$.complete();
      }
}
